import {ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexStroke,
  ApexGrid,
  ChartComponent,
  ApexPlotOptions,
  ApexYAxis,
  ApexTooltip,
  ApexFill,
  ApexTheme
} from "ng-apexcharts";
import { NgApexchartsModule } from "ng-apexcharts";
import {Company} from "../../../../interfaces/company.interface";
import {getAssistantPromptByTag} from "../../../../utils/assistant-prompt.util";
import {NgClass, NgIf} from "@angular/common";
import {PopupService} from "../../../../services/popup.service";

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  fill: ApexFill;
  plotOptions: ApexPlotOptions;
  theme: ApexTheme;
};

@Component({
  selector: 'app-department-job-types',
  standalone: true,
  imports: [NgApexchartsModule, NgIf, NgClass],
  styles: [`
    .animated-random-gradient-text {
      background: linear-gradient(90deg,
        var(--gradient-start),
        var(--gradient-mid),
        var(--gradient-end)
      );
      background-size: 400% auto;
      color: transparent;
      -webkit-background-clip: text;
      background-clip: text;
      animation: randomGradientShift 5s ease infinite;
    }
    .animated-dots::after {
      content: '...';
      display: inline-block;
      animation: loadingDots 1.5s infinite linear;
      overflow: hidden;
      color: transparent;
      vertical-align: bottom;
    }
    @keyframes randomGradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes loadingDots {
      0%, 20% { content: '.'; }
      33%, 53% { content: '..'; }
      66%, 86% { content: '...'; }
    }
  `],
  template: `
    <div id="department-job-types">
      <ng-container *ngIf="hasData; else noData">
        <div class="px-6 !text-secondary">
          <span class="text-lg font-semibold !text-secondary">{{ totalYears }}</span> total years
        </div>
        <apx-chart
          [series]="chartOptions.series"
          [chart]="chartOptions.chart"
          [dataLabels]="chartOptions.dataLabels"
          [plotOptions]="chartOptions.plotOptions"
          [yaxis]="chartOptions.yaxis"
          [xaxis]="chartOptions.xaxis"
          [grid]="chartOptions.grid"
          [fill]="chartOptions.fill"
          [tooltip]="chartOptions.tooltip"
          [theme]="chartOptions.theme"
        ></apx-chart>
        <button
          *ngIf="allDepartments?.length"
          (click)="showAllDepartments()"
          class="absolute bottom-3 right-3 text-sm text-blue-500 hover:opacity-90 mt-2 text-center">
          Show All
        </button>
      </ng-container>
      <ng-template #noData>
        <div class="flex items-center justify-start text-gray-500 px-6">
          <!-- {{ loadingState ? 'Loading...' : 'N/A' }} -->

          <!-- Loading -->
          <div *ngIf="loadingState" class="flex items-center space-x-3">

            <div class="flex items-center space-x-3">
              <span [ngClass]="['relative', 'flex', 'items-center', size]">
                <span [ngClass]="['animate-ping', 'absolute', 'inline-flex', 'rounded-full', size, 'bg-' + color, 'opacity-75']"></span>
                <span [ngClass]="['relative', 'inline-flex', 'rounded-full', size, 'bg-' + color]"></span>
              </span>
              <p class="relative text-sm !text-secondary dark:text-white loading-text">
                <span class="animated-random-gradient-text">
                  Gathering data<span class="animated-dots"></span>
                </span>
              </p>
            </div>

          </div>

          <span *ngIf="!loadingState">N/A</span>

        </div>
      </ng-template>
    </div>
  `
})
export class DepartmentJobTypesComponent implements OnInit, OnChanges {
  @ViewChild("chart") chart!: ChartComponent;
  // public chartOptions: Partial<ChartOptions>;
  @Input() company: Company;

  public chartOptions: any = null;
  areas_of_expertise: { series: any[]; labels: string[] } = { series: [], labels: [] };

  allDepartments: { name: string; years: number }[] = [];
  totalYears: number = 0;

  hasData: boolean = false;
  loadingState: boolean = false;

  @Input() size: string = 'size-2';
  @Input() color: string = 'blue-sky';

  constructor(private popupService: PopupService, private cdr: ChangeDetectorRef) {
    this.initChartOptions();
  }

  ngOnInit(): void {}

  ngOnDestroy() {
    // Cleanup if needed
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['company']?.currentValue) {
      const previousValue = changes['company'].previousValue;
      const currentValue = changes['company'].currentValue;

      // Check both insights and assistant_prompts for the data
      const previousData = this.getPromptData(previousValue);
      const currentData = this.getPromptData(currentValue);

      // Compare the actual data, not just the reference
      if (JSON.stringify(previousData) !== JSON.stringify(currentData)) {
        if (currentValue) {
          // Check if data exists in either insights or assistant_prompts
          const hasInsight = currentValue.insights?.department_years?.response;
          const hasPrompt = currentValue.assistant_prompts?.some(
            (item: any) => item.tag === 'ASK_ASSISTANT_DEPARTMENT_YEARS'
          );

          if (!hasInsight && !hasPrompt) {
            this.hasData = false;
            this.loadingState = true;
          } else {
            this.loadingState = false;
            this.initApexCharts(currentValue);
          }
        }
      }
    }
  }

  private getPromptData(company: any): any {
    if (!company) return null;
    // Try insights first (from WebSocket), then fall back to assistant_prompts
    return company.insights?.department_years?.response ||
           company.assistant_prompts?.find((item: any) => item.tag === 'ASK_ASSISTANT_DEPARTMENT_YEARS')?.content;
  }

  private initApexCharts(company: any) {
    const promptContent = getAssistantPromptByTag(company, 'ASK_ASSISTANT_DEPARTMENT_YEARS');
    if (promptContent) {
      this.getAreasOfExpertise(promptContent);
    }
  }

  private getAreasOfExpertise(areasOfExpertise: any): void {
    try {
      if (!areasOfExpertise) {
        this.hasData = false;
        this.totalYears = 0;
        return;
      }

      let parsedData = areasOfExpertise;
      if (typeof areasOfExpertise === 'string' && this.isValidJSON(areasOfExpertise)) {
        parsedData = JSON.parse(areasOfExpertise);
      }

      // Check if we have valid data
      if (!parsedData?.top_5?.series?.length || !parsedData?.top_5?.labels?.length) {
        this.hasData = false;
        this.totalYears = 0;
        return;
      }

      // Store all departments
      if (parsedData.all) {
        this.allDepartments = parsedData.all;
        // Calculate total years from all departments
        this.totalYears = parsedData.all.reduce((sum: number, dept: any) => sum + (dept.years || 0), 0);
      }

      // Handle the chart data
      if (parsedData.top_5) {
        this.areas_of_expertise = {
          series: parsedData.top_5.series,
          labels: parsedData.top_5.labels
        };
        this.hasData = true;
      }

      this.updateChartData();
    } catch (error) {
      console.error("[getAreasOfExpertise] Failed to process data", error);
      this.hasData = false;
      this.totalYears = 0;
    }
  }

  private isValidJSON(jsonString: string): boolean {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (error) {
      return false;
    }
  }

  private showNoDataMessage(): void {
    this.areas_of_expertise = { series: [0], labels: ['N/A'] };
    this.allDepartments = [];
    this.updateChartData();
  }

  private updateChartData() {
    if (this.areas_of_expertise?.series && this.areas_of_expertise.labels) {
      this.chartOptions.series = [{
        name: "Years of Experience",
        data: this.areas_of_expertise.series
      }];

      this.chartOptions.xaxis = {
        categories: this.areas_of_expertise.labels,
        labels: {
          show: false
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      };

      // Add more spacing for yaxis
      this.chartOptions.yaxis = {
        ...this.chartOptions.yaxis,
        labels: {
          ...this.chartOptions.yaxis.labels,
          offsetY: 2.5,
          offsetX: 10, // Adjust label position if needed
        },
        tickAmount: this.areas_of_expertise.labels.length, // Match number of items
        min: 0, // Add padding at top
        max: this.areas_of_expertise.labels.length + 2 // Add padding at bottom
      };

      this.cdr.detectChanges();
    }
  }

  showAllDepartments(): void {
    if (!this.allDepartments?.length) return;

    const formattedContent = `
      <div>
        <div class="divide-y divide-gray-100">
          ${this.allDepartments.map(area => `
            <div class="flex justify-between items-center py-2">
              <div class="flex-1">
                <span class="text-secondary">${area.name}</span>
              </div>
              <div class="ml-4">
                <span class="font-semibold text-secondary">${area.years} ${area.years === 1 ? 'year' : 'years'}</span>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;

    this.popupService.info('Department Experience', formattedContent);
  }

  private initChartOptions() {
    this.chartOptions = {
      series: [{
        name: "Areas of Expertise",
        data: []
      }],
      chart: {
        type: "bar",
        height: 300,
        toolbar: {
          show: false
        }
      },
      theme: {
        monochrome: {
          enabled: true,
          color: '#4B5563',
          shadeTo: 'light',
          shadeIntensity: 0.65
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: [],
        labels: {
          show: false
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px',
            fontFamily: 'Poppins, sans-serif',
            colors: '#3c4277'
          }
        }
      },
      fill: {
        opacity: 1,
        colors: ['#2c7fff']
      },
      tooltip: {
        enabled: true
      },
      grid: {
        show: false
      },
    };
  }

  /*
  private initChartOptions() {

    this.chartOptions = {
      series: [
        {
          name: "Basic",
          data: [400, 430, 448, 470, 540]
        }
      ],
      chart: {
        type: "bar",
        height: 200,
        toolbar: {
          show: false
        }
      },
      theme: {
        monochrome: {
          enabled: true,
          color: '#4B5563',
          shadeTo: 'light',
          shadeIntensity: 0.65
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: [
          "South Korea",
          "Canada",
          "United Kingdom",
          "Netherlands",
          "Italy",
        ],
        labels: {
          show: false
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px',
            fontFamily: 'Poppins, sans-serif',
            colors: '#3c4277'
          }
        }
      },
      fill: {
        opacity: 1,
        colors: ['#2c7fff']
      },
      tooltip: {
        enabled: true
      },
      grid: {
        show: false
      }
    };

  }
  */
}
